export function getTotalComments(comments = []) {
  return comments.reduce((sum, comment) => {
    const visibleCount = comment.deleted ? 0 : 1;
    return sum + visibleCount + getTotalComments(comment.replies || []);
  }, 0);
}

export function makeThreadMessage({ user, profile, side, text, image }) {
  const displayName = profile?.displayName || user.displayName || 'Anonymous';
  return {
    id: `${Date.now()}-${user.uid}-${Math.random().toString(16).slice(2)}`,
    userId: user.uid,
    user: displayName,
    userPhoto: profile?.photoURL || user.photoURL || '',
    side,
    text: text || '',
    image: image || null,
    likes: 0,
    likedBy: [],
    replies: [],
    createdAt: new Date().toISOString()
  };
}

export function updateThreadItem(items = [], path = [], updater) {
  const [targetIndex, ...restPath] = path;
  return items.map((item, index) => {
    if (index !== targetIndex) return item;
    if (restPath.length === 0) return updater(item);
    return {
      ...item,
      replies: updateThreadItem(item.replies || [], restPath, updater)
    };
  });
}

export function getThreadItem(items = [], path = []) {
  let list = items;
  let target = null;

  for (const index of path) {
    target = list[index];
    if (!target) return null;
    list = target.replies || [];
  }

  return target;
}

export function deleteThreadItem(items = [], path = []) {
  const [targetIndex, ...restPath] = path;

  if (targetIndex === undefined) return items;

  if (restPath.length === 0) {
    return items.flatMap((item, index) => {
      if (index !== targetIndex) return [item];
      const replies = item.replies || [];

      if (replies.length > 0) {
        return [{
          ...item,
          text: '',
          image: null,
          likes: 0,
          likedBy: [],
          deleted: true,
          deletedAt: new Date().toISOString()
        }];
      }

      return [];
    });
  }

  return items.map((item, index) => {
    if (index !== targetIndex) return item;
    return {
      ...item,
      replies: deleteThreadItem(item.replies || [], restPath)
    };
  });
}

function collectReplyNotifications(post, comment, userId, output = []) {
  (comment.replies || []).forEach(reply => {
    if (comment.userId === userId && reply.userId !== userId) {
      output.push({
        id: `${post.id}-${reply.id || reply.createdAt}-reply`,
        type: 'reply',
        title: `${reply.user || 'Someone'} replied to you`,
        body: reply.text || 'Sent an image reply',
        meta: post.question,
        createdAt: reply.createdAt || '',
        post
      });
    }
    collectReplyNotifications(post, reply, userId, output);
  });
  return output;
}

export function buildNotifications(posts = [], userId, preferences = {}) {
  if (!userId) return [];
  const allowComments = preferences.comments !== false;
  const allowReplies = preferences.replies !== false;
  const allowHeat = preferences.heat !== false;
  const items = [];
  posts.forEach(post => {
    const comments = post.comments || [];
    if (post.authorId === userId && allowComments) {
      comments.forEach(comment => {
        if (comment.userId && comment.userId !== userId) {
          items.push({
            id: `${post.id}-${comment.id || comment.createdAt}-comment`,
            type: 'comment',
            title: `${comment.user || 'Someone'} commented on your Hix`,
            body: comment.text || 'Sent an image comment',
            meta: post.question,
            createdAt: comment.createdAt || '',
            post
          });
        }
      });
      if (allowHeat && (post.heatCount || 0) > 0) {
        items.push({
          id: `${post.id}-heat-${post.heatCount || 0}`,
          type: 'heat',
          title: 'Your Hix is getting heat',
          body: `${post.heatCount || 0} people heated it`,
          meta: post.question,
          createdAt: post.updatedAt?.toDate?.().toISOString?.() || '',
          post
        });
      }
    }
    if (allowReplies) {
      comments.forEach(comment => collectReplyNotifications(post, comment, userId, items));
    }
  });
  return items
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
    .slice(0, 12);
}
